/**
 * 契約詳細のブラウザでのバリデーション（プロダクト仕様書「入力値の規則」）。
 * サーバーのチェックのうち、入力欄ごとのチェックを行う。エラーの対象が「なし」のチェック
 * （料金プランで使わない欄が空欄か、適用中の行の開始月とクレジット数、期間の重なり）は、サーバーだけで行う。
 */

import type { FaceValue, RowInput } from '@/lib/api/credit-contracts';
import { parseNumberInput, trimWhiteSpace } from '@/lib/credit-conversion-settings/number';

import { messages } from './messages';
import { overlaps, parseMonthInput, type Period } from './month';

export type FieldKey = keyof RowInput;
export type FieldErrors = Partial<Record<FieldKey, string>>;

/**
 * どの行をどう保存するか。データコネクトのクレジット数は、追加と、予定の行・適用中の行の編集で、チェックが違う
 */
export type EditMode =
  | { table: 'pricing-plans' }
  | { table: 'data-connect-credits'; kind: 'add' | 'scheduled' | 'active' };

/** 月の入力欄の値（`YYYY/MM`）を `YYYY-MM` にする。空欄なら null、`YYYY/MM` の月でなければ undefined */
function monthOf(input: string | undefined): string | null | undefined {
  const text = trimWhiteSpace(input ?? '');
  if (text === '') return null;
  return parseMonthInput(text) ?? undefined;
}

/** クレジットの形式で、0より大きい数値か */
function checkCredit(input: string | undefined): string | null {
  const result = parseNumberInput(input ?? '');
  if (!result.ok) return result.blank ? messages.numberBlank : messages.creditFormat;
  const [int, frac = ''] = result.canonical.split('.');
  const intDigits = int === '0' ? 0 : int.length;
  if (intDigits > 10 || frac.length > 8) return messages.creditDigits;
  if (result.canonical === '0') return messages.aboveZero;
  return null;
}

/** 円の形式（小数点なし、2147483647以下）で、`min` 以上の整数か */
function checkYen(input: string | undefined, min: 0 | 1): string | null {
  const text = input ?? '';
  const result = parseNumberInput(text);
  if (!result.ok) return result.blank ? messages.numberBlank : messages.yenFormat;
  if (text.includes('.')) return messages.yenFormat;
  const value = result.canonical;
  if (value.length > 10 || (value.length === 10 && value > '2147483647')) return messages.yenMax;
  if (min === 1 && value === '0') return messages.atLeastOne;
  return null;
}

/**
 * 入力欄ごとのチェック。入力欄ごとに最初のエラー1つを返す。`current` はサーバーが求めた当月
 */
export function checkFields(input: RowInput, mode: EditMode, current: string): FieldErrors {
  const errors: FieldErrors = {};
  const dataConnect = mode.table === 'data-connect-credits' ? mode.kind : null;

  const start = monthOf(input.start_month);
  if (start === null) errors.start_month = messages.startBlank;
  else if (start === undefined) errors.start_month = messages.startFormat;
  else if ((dataConnect === 'add' || dataConnect === 'scheduled') && start <= current) {
    errors.start_month = messages.startNotAfterCurrent(current);
  }

  // 開始月が `YYYY/MM` の月なら、開始月にほかのエラーがあっても、終了月と比べる（サーバーと同じ）
  const end = monthOf(input.end_month);
  if (end === undefined) errors.end_month = messages.endFormat;
  else if (end !== null && dataConnect === 'active') {
    if (end < current) errors.end_month = messages.activeEndBeforeCurrent(current);
  } else if (end !== null && start && end < start) {
    errors.end_month = messages.endBeforeStart(start);
  }

  if (mode.table === 'pricing-plans') {
    if (input.pricing_plan === 'Credit') {
      const credit = checkCredit(input.monthly_contract_credit);
      if (credit) errors.monthly_contract_credit = credit;
      const price = checkYen(input.credit_unit_price, 1);
      if (price) errors.credit_unit_price = price;
    } else if (input.pricing_plan === 'FixedFee') {
      const fee = checkYen(input.monthly_fee, 0);
      if (fee) errors.monthly_fee = fee;
    } else {
      errors.pricing_plan = messages.planRequired;
    }
  } else if (dataConnect !== 'active') {
    const credit = checkCredit(input.monthly_credit);
    if (credit) errors.monthly_credit = credit;
  }
  return errors;
}

export function hasErrors(errors: FieldErrors): boolean {
  return Object.keys(errors).length > 0;
}

/** 入力値の期間（`YYYY-MM` の月）。入力欄のチェックでエラーがないときだけ使う */
export function periodOf(input: RowInput): Period {
  return {
    start_month: monthOf(input.start_month) ?? '',
    end_month: monthOf(input.end_month) ?? null,
  };
}

/**
 * 追加で終了月を合わせる行：追加する行の期間が、ほかの行1つとだけ重なり、その行が先に始まるとき、その行。
 * 合わせた後の終了月は、追加する行の開始月の前の月
 */
export function adjustTarget<T extends Period>(rows: T[], added: Period): T | null {
  const overlapping = rows.filter((row) => overlaps(row, added));
  if (overlapping.length !== 1) return null;
  const [row] = overlapping;
  return row.start_month < added.start_month ? row : null;
}

/** 開始月の1日に有効な1クレジットの額面。なければ null */
export function faceValueAt(faces: FaceValue[], month: string): number | null {
  const day = `${month}-01`;
  let found: number | null = null;
  for (const face of faces) {
    if (face.effective_dt <= day) found = face.credit_face_value;
  }
  return found;
}

/** クレジット数に額面を掛けた円の額。浮動小数点数にせず、10進の文字列で求める */
export function yenOf(credit: string, faceValue: number): string {
  const [int, frac = ''] = credit.split('.');
  const scaled = (BigInt(`${int}${frac}`) * BigInt(faceValue))
    .toString()
    .padStart(frac.length + 1, '0');
  const intPart = scaled.slice(0, scaled.length - frac.length);
  const fracPart = scaled.slice(scaled.length - frac.length).replace(/0+$/, '');
  return fracPart ? `${intPart}.${fracPart}` : intPart;
}
